import { STARTER } from "../lib/coins";

// Nebula hue per character, picked to complement each uniform / prop.
export const GALAXY_HUE = {
  baby_barnes: 142, private_garcia: 95, private_aspirin: 35, corporal_narcan: 275,
  nurse_atropine: 345, chief_oxygen: 190, doc_dextrose: 28, sgt_amiodarone: 215,
  captain_sinus: 142, lt_levophed: 200, major_adenosine: 52, commander_torsades: 285,
  captain_lee: 225, general_fibula: 45,
};

const starsA = (h) => [
  "radial-gradient(#ffffffd9 0.55px, transparent 1.1px) 3px 5px / 23px 29px",
  `radial-gradient(hsl(${h} 100% 88% / .95) 0.8px, transparent 1.5px) 19px 9px / 47px 53px`,
].join(",");
const starsB = () => "radial-gradient(#ffffff99 0.45px, transparent 1px) 11px 17px / 31px 37px";
const nebula = (h) => [
  `radial-gradient(ellipse 55% 45% at 50% 44%, hsl(${h} 85% 72% / .5), transparent 70%)`,
  `radial-gradient(ellipse 60% 40% at 18% 22%, hsl(${(h + 40) % 360} 95% 60% / .5), transparent 65%)`,
  `radial-gradient(ellipse 55% 45% at 85% 80%, hsl(${(h + 320) % 360} 90% 55% / .45), transparent 65%)`,
].join(",");
const base = (h) => `radial-gradient(circle at 50% 50%, hsl(${h} 70% 22%), #04060f 90%)`;

export const galaxyCss = (h = 230) => [starsA(h), starsB(), nebula(h), base(h)].join(",");

export const AVATAR_BGS = [
  { id: "galaxy", label: "Galaxy", css: galaxyCss(230) },
  { id: "none", label: "Transparent", css: "transparent" },
  { id: "paper", label: "Paper", css: "#f7f5f0" },
  { id: "slate", label: "Slate", css: "linear-gradient(160deg,#1f2937,#0b0f14)" },
  { id: "phosphor", label: "Phosphor", css: "radial-gradient(circle at 50% 40%,#1b5e34,#07110b)" },
  { id: "amber", label: "Amber", css: "radial-gradient(circle at 50% 40%,#b45309,#1a0f02)" },
  { id: "sky", label: "Sky", css: "linear-gradient(160deg,#0ea5e9,#0b1a2b)" },
  { id: "violet", label: "Violet", css: "linear-gradient(160deg,#7c3aed,#140a2b)" },
  { id: "rose", label: "Rose", css: "linear-gradient(160deg,#e11d48,#2b0a12)" },
  { id: "camo", label: "Camo", css: "repeating-linear-gradient(45deg,#3f4a2a 0 12px,#5b6b3a 12px 24px,#2b331c 24px 36px)" },
];

export const bgCss = (id, avatarId) => {
  if (!id || id === "galaxy") return galaxyCss(GALAXY_HUE[avatarId] ?? 230);
  return AVATAR_BGS.find((b) => b.id === id)?.css || "transparent";
};

function AnimatedGalaxy({ hue }) {
  const layer = "absolute inset-[-25%] pointer-events-none";
  return (
    <>
      <span aria-hidden className={`${layer} galaxy-nebula`} style={{ background: nebula(hue) }} />
      <span aria-hidden className={`${layer} galaxy-stars-a`} style={{ background: starsA(hue) }} />
      <span aria-hidden className={`${layer} galaxy-stars-b`} style={{ background: starsB() }} />
    </>
  );
}

export function Avatar({ id, bg = "galaxy", size = 40, className = "", ring = false, animated = false, testid = "avatar" }) {
  const avatarId = id || STARTER;
  const bgId = bg || "galaxy";
  const live = animated && bgId === "galaxy";
  const hue = GALAXY_HUE[avatarId] ?? 230;
  return (
    <div
      data-testid={testid}
      data-avatar-id={avatarId}
      data-avatar-bg={bgId}
      data-animated={live || undefined}
      style={{ width: size, height: size, background: live ? base(hue) : bgCss(bgId, avatarId) }}
      className={`relative shrink-0 overflow-hidden border ${ring ? "border-phosphor shadow-[0_0_14px_hsl(142_71%_45%/0.5)]" : "border-border"} ${className}`}
    >
      {live && <AnimatedGalaxy hue={hue} />}
      <img
        src={`/avatars/${avatarId}.webp`}
        alt={avatarId}
        width={size}
        height={size}
        onError={(e) => { if (!e.currentTarget.src.endsWith(`${STARTER}.webp`)) e.currentTarget.src = `/avatars/${STARTER}.webp`; }}
        className="relative w-full h-full object-contain p-[4%] drop-shadow-[0_6px_8px_rgba(0,0,0,0.7)]"
      />
    </div>
  );
}
